import React, { useState, useRef } from "react";
import { Redirect } from "react-router-dom";
import { Form, StringField, ImageField, DateTimeField, TimezoneField, SubmitButton, classnames } from "@culturehq/components";

import { Trans, t } from "app/utils";

import LazyEditor from "components/editor/LazyEditor";

import EventPortalButton from "../common/EventPortalButton";
import EventForm from "./EventForm";
import styles from "./EventForm.module.scss";

const EventBasicInfo = ({ eventState, handleEventState, handleImage, onSubmit, handleUnSaved }) => {
  const editorRef = useRef(null);
  const [startsAt, setStartsAt] = useState(eventState.startsAt);
  const [endsAt, setEndsAt] = useState(eventState.endsAt);
  const [timezoneOffset, setTimezoneOffset] = useState(-new Date().getTimezoneOffset());
  if (!eventState.eventType) {
    return <Redirect to="/events/new/type" />;
  }

  const getInitialValues = () => {
    const image = (eventState.image && eventState.image.originalUrl)
      ? eventState.image.originalUrl
      : eventState.image;
    return {
      ...eventState,
      image
    };
  };

  const handleSubmit = async ({ image, imageBlob, ...values }) => {
    await handleUnSaved(false);
    const details = await editorRef.current.serialize();
    if (eventState.id) {
      await onSubmit({ ...values, details, endsAt });
    } else {
      handleEventState({ ...values, details, endsAt });
    }
  };

  const handleImageChange = val => {
    handleUnSaved(true);
    const imageBlob = (val && val.size)
      ? val
      : null;
    const event_image = val && val.size
      ? URL.createObjectURL(val)
      : val;
    handleImage(event_image, imageBlob);
    getInitialValues();
  };

  const handleStartsAtChange = newStartsAt => {
    handleUnSaved(true);
    const diff = +new Date(newStartsAt) - +new Date(startsAt);
    const endsDate = new Date(+new Date(endsAt) + diff);
    setStartsAt(newStartsAt);
    setEndsAt(endsDate);
  };

  const handleValidateEndsAt = newEndsAt => {
    if (startsAt && new Date(startsAt) > new Date(newEndsAt)) {
      return t("Event's start time must be before the end time.");
    }
    return null;
  };

  const handleEndsAtChange = endDate => {
    handleUnSaved(true);
    setEndsAt(endDate);
  };

  const handleOffsetChange = timezone => {
    setTimezoneOffset(timezone);
  };

  return (
    <EventForm icon="clipboard" title={t("What are the basic details?")}>
      <p className={classnames(styles["m-b-35"], styles["f-s-18"])}>
        <Trans>
          Include the general information about your event, upload an image to
          generate excitement, and provide a nice description to outline the
          purpose and details.
        </Trans>
      </p>
      <Form onSubmit={handleSubmit} initialValues={getInitialValues()} id="event_basic_form" className="event-form">
        <StringField className="m-b-20" name="name" onChange={() => handleUnSaved(true)} required autoFocus>
          <Trans>Event Name *</Trans>
        </StringField>
        <ImageField name="image" className="b-color" aspectRatio={16 / 9} onChange={handleImageChange} required>
          <label className={styles.customLabel}><Trans>Event Image *</Trans></label>
        </ImageField>
        <div className={classnames(styles["m-b-20"], "chq-event-edit--times", "row")}>
          <div className="col-sm-6 col-md-4">
            <DateTimeField
              ariaLabel={t("Select event visibility")}
              name="startsAt"
              offset={timezoneOffset}
              value={startsAt}
              onChange={handleStartsAtChange}
              required
            >
              <Trans>Event Starts At</Trans>
            </DateTimeField>
          </div>
          <div className="col-sm-6 col-md-4">
            <DateTimeField
              name="endsAt"
              offset={timezoneOffset}
              value={endsAt}
              onChange={handleEndsAtChange}
              validator={handleValidateEndsAt}
              required
            >
              <Trans>Event Ends At</Trans>
            </DateTimeField>
          </div>
          <div className="col-sm-12 col-md-4">
            <TimezoneField
              name="timezone"
              onChange={() => handleUnSaved(true)}
              onOffsetChange={handleOffsetChange}
              required
            >
              <Trans>Event Time Zone</Trans>
            </TimezoneField>
          </div>
        </div>
        <StringField aria-label={t("Enter where the event will be held?")} name="location" onChange={() => handleUnSaved(true)}>
          <Trans>Where</Trans>
        </StringField>
        <div className={classnames(styles["m-b-30"], "chq-ffd")}>
          <span className="chq-ffd--lb">
            <Trans>Description</Trans>
          </span>
          <LazyEditor
            ref={editorRef}
            initialValue={eventState.details}
            onChange={() => handleUnSaved(true)}
            placeholder={t("Enter the event description")}
          />
        </div>
        <EventPortalButton>
          {eventState.id
            ? (
              <SubmitButton primary form="event_basic_form">
                {submitting => (submitting ? t("Saving...") : t("Save Event"))}
              </SubmitButton>
            )
            : (
              <SubmitButton primary form="event_basic_form">
                {submitting => (submitting ? t("Go next...") : t("Next"))}
              </SubmitButton>
            )}
        </EventPortalButton>
      </Form>
    </EventForm>
  );
};

export default EventBasicInfo;
